import { useState, useRef, useEffect, ReactNode } from 'react';
import { Info } from 'lucide-react';

interface Props {
    title: string;
    children: ReactNode;
}

export default function InfoIcon({ title, children }: Props) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const onEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('mousedown', onClickOutside);
        window.addEventListener('keydown', onEscape);
        return () => {
            window.removeEventListener('mousedown', onClickOutside);
            window.removeEventListener('keydown', onEscape);
        };
    }, [open]);

    return (
        <span ref={ref} className="relative inline-flex items-center">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                onMouseEnter={() => setOpen(true)}
                className="text-gray-400 hover:text-blue-600 transition-colors"
                aria-label={`Info: ${title}`}
            >
                <Info className="w-4 h-4" />
            </button>
            {open && (
                <div
                    onMouseLeave={() => setOpen(false)}
                    className="absolute left-5 top-0 z-50 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700 leading-relaxed"
                >
                    <div className="font-semibold text-gray-900 mb-1">{title}</div>
                    <div className="space-y-1">{children}</div>
                </div>
            )}
        </span>
    );
}
